import { Address } from 'interfaces/address';
import { Customer, CustomerCredit, SavingsPlan } from 'interfaces/customer';
import { axios } from 'utils/axios';
import * as AddressModel from '../models/address';
import * as OrdersModel from '../models/order';
import camelCaseKeys from 'camelcase-keys';

export interface FetchCustomerResponse extends CustomerCredit {
  acceptsMarketing: boolean;
  /** created date in unix time stamp */
  acceptsMarketingUpdatedAt: string;
  addresses: Array<Address>;
  adminGraphqlApiId: string;
  /** created date in unix time stamp */
  createdAt: string;
  currency: string;
  defaultAddress?: Address;
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  lastOrderId?: number;
  lastOrderName?: string;
  marketingOptInLevel: any;
  multipassIdentifier: any;
  note: any;
  ordersCount: number;
  phone: any;
  state: string;
  /** Formatted with ","  */
  tags: string;
  taxExempt: boolean;
  taxExemptions: Array<any>;
  /** Sum of total spend in the format with cent */
  totalSpent: string;
  /** Updated date in unix time stamp */
  updatedAt: '2022-07-01T11:20:32+02:00';
  verifiedEmail: boolean;
  savingsPlan?: SavingsPlan;
}

export const fetchCustomer = (customerId: number): Promise<FetchCustomerResponse> =>
  axios.get<FetchCustomerResponse>(`account/customer/${customerId}`).then(({ data }) => data);

export interface AddressResponse {
  address1: string;
  /** @deprecated */
  address2: string;
  city: string;
  company: string;
  country: string;
  countryCode: string;
  countryName: string;
  customerId: number;
  default: boolean;
  firstName: string;
  id: number;
  lastName: string;
  name: string;
  phone: string;
  /** @deprecated */
  province: string;
  /** @deprecated */
  provinceCode: string;
  zip: string;
}

export const updateAddress = (customerId: number, address: Address) =>
  axios
    .post<AddressResponse>(`/account/address/${customerId}`, AddressModel.toServerFormat(address))
    .then(({ data }) => AddressModel.fromServerFormat(data));

export const createAddress = updateAddress;

export const deleteAddress = (customerId: number, addressId: number) =>
  axios.delete(`/account/address/${customerId}`, {
    data: {
      id: addressId,
    },
  });

export const fetchOrders = (customerId: number) =>
  axios
    .get(`/api/store/customers/${customerId}/orders`, {
      isPdexApi: true,
    })
    .then(({ data }) => {
      return camelCaseKeys(data.orders, { deep: true });
    })
    .then(OrdersModel.ordersFromServerFormat);

export const fetchOrderDetails = (customerId: number, orderId: number) =>
  axios
    .get(`/api/store/customers/${customerId}/orders/${orderId}`, {
      isPdexApi: true,
    })
    .then(({ data }) => {
      return camelCaseKeys(data.order, { deep: true });
    })
    .then(OrdersModel.orderDetailsFromServerFormat);

interface PasswordForgotResult {
  success: boolean;
  message?: string;
}

/**
 * Send a password reset email to customer.
 */
export const passwordForgot = async (email: string): Promise<PasswordForgotResult> =>
  axios
    .post(
      '/api/customers',
      {
        action: 'forgot',
        email,
      },
      {
        isPdexApi: true,
      }
    )
    .then(({ data }) => {
      if (!data) {
        return {
          success: false,
          message: data.message || 'An unknown error occurred.',
        };
      }

      return {
        success: true,
      };
    });

/**
 * Reset password for customer.
 */
export const passwordReset = (
  password: string,
  password_confirmation: string,
  token: string
): Promise<{ customer: Pick<Customer, 'id' | 'email'> }> =>
  axios
    .post(
      '/api/customers',
      {
        action: 'reset',
        password,
        password_confirmation,
        token,
      },
      {
        isPdexApi: true,
      }
    )
    .then(({ data }) => data);

/**
 * Activate account for customer.
 */
export const activateAccount = (
  password: string,
  password_confirmation: string,
  token: string
): Promise<{ customer: Pick<Customer, 'id' | 'email'> }> =>
  axios
    .post(
      '/api/customers',
      {
        action: 'confirm',
        password,
        password_confirmation,
        token,
      },
      {
        isPdexApi: true,
      }
    )
    .then(({ data }) => data);
